import type { ReactNode, CSSProperties, ChangeEvent } from 'react';
import './ToolbarPill.css';

/* ─────────────────────────────────────────────────────────────────────────────
 * PillToggle — exclusive selection group rendered as a pill
 *
 * Multiple options, one active at a time. `disabled` locks the whole group.
 * Covers: Brush/Line, Apply/Cancel, Size/Arrange/Draw/Image, etc.
 * ───────────────────────────────────────────────────────────────────────────── */

interface PillToggleOption<T extends string | number = string> {
  value: T;
  label: ReactNode;
  'aria-label'?: string;
}

interface PillToggleProps<T extends string | number = string> {
  options: PillToggleOption<T>[];
  value: T;
  onChange: (value: T) => void;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
  'aria-label'?: string;
}

/** Pill-shaped exclusive selection group — one option active at a time */
export function PillToggle<T extends string | number = string>({
  options,
  value,
  onChange,
  disabled,
  className,
  style,
  'aria-label': ariaLabel,
}: PillToggleProps<T>) {
  return (
    <div
      className={['ui-pill-toggle', disabled ? 'is-disabled' : '', className ?? ''].filter(Boolean).join(' ')}
      role="radiogroup"
      aria-label={ariaLabel}
      style={style}
    >
      {options.map((opt) => (
        <button
          key={String(opt.value)}
          className={`ui-pill-toggle__item${opt.value === value ? ' is-active' : ''}`}
          role="radio"
          aria-checked={opt.value === value}
          aria-label={opt['aria-label']}
          disabled={disabled}
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
 * PillButton — standalone pill-shaped button
 *
 * Variants:
 *   default  — warm neutral (hover: lighter warm)
 *   danger   — same at rest, red hover (for destructive actions)
 *
 * Optional slots:
 *   icon     — leading glyph/SVG rendered before the label
 *   badge    — trailing count/status chip (e.g. remaining hints)
 *
 * Flags:
 *   isActive — toggle pressed state
 *   quiet    — subdued styling for read-only readouts (e.g. elapsed time)
 *
 * Covers: Import image, Invert, Remove all, Single puzzle, Split 5×5, plus
 * the in-game top toolbar (Pause/Resume, Check, Hint) and quiet readouts.
 * ───────────────────────────────────────────────────────────────────────────── */

interface PillButtonProps {
  children: ReactNode;
  variant?: 'default' | 'danger';
  /** Leading icon slot (rendered before children). */
  icon?: ReactNode;
  /** Trailing count/status badge (rendered after children). */
  badge?: ReactNode;
  /** Toggle pressed state. */
  isActive?: boolean;
  /** Subdued styling — read-only readouts like elapsed time. */
  quiet?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  'aria-label'?: string;
  'aria-pressed'?: boolean;
  className?: string;
  style?: CSSProperties;
}

/** Standalone pill-shaped button — action, toggle, or labelled readout. */
export function PillButton({
  children,
  variant = 'default',
  icon,
  badge,
  isActive,
  quiet,
  disabled,
  onClick,
  'aria-label': ariaLabel,
  'aria-pressed': ariaPressed,
  className,
  style,
}: PillButtonProps) {
  return (
    <button
      className={[
        'ui-pill-button',
        variant === 'danger' ? 'ui-pill-button--danger' : '',
        quiet ? 'ui-pill-button--quiet' : '',
        isActive ? 'is-active' : '',
        className ?? '',
      ].filter(Boolean).join(' ')}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-pressed={ariaPressed}
      style={style}
    >
      {icon && (
        <span className="ui-pill-button__icon" aria-hidden="true">
          {icon}
        </span>
      )}
      <span className="ui-pill-button__label">{children}</span>
      {badge != null && <span className="ui-pill-button__badge">{badge}</span>}
    </button>
  );
}

// Backwards-compatible alias for older pill-group item usage.
export const PillButtonItem = PillButton;

/* ─────────────────────────────────────────────────────────────────────────────
 * PillSlider — pill with label + range slider + value readout
 * ───────────────────────────────────────────────────────────────────────────── */

interface PillSliderProps {
  label?: string;
  min?: number;
  max?: number;
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
  /** Shorter height and font for tight toolbars */
  compact?: boolean;
  /** Custom value formatter — defaults to the raw number */
  formatValue?: (v: number) => string;
  'aria-label'?: string;
}

/** Pill containing a labelled range slider with value readout */
export function PillSlider({
  label,
  min = 0,
  max = 100,
  value,
  onChange,
  disabled,
  compact,
  formatValue,
  'aria-label': ariaLabel,
}: PillSliderProps) {
  const display = formatValue ? formatValue(value) : String(value);

  return (
    <div
      className={[
        'ui-pill-slider',
        compact ? 'ui-pill-slider--compact' : '',
        disabled ? 'is-disabled' : '',
      ].filter(Boolean).join(' ')}
      role="group"
      aria-label={ariaLabel ?? label}
    >
      {label && <span className="ui-pill__label">{label}</span>}
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        disabled={disabled}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(Number(e.target.value))}
        aria-label={ariaLabel ?? label}
      />
      <span className="ui-pill-slider__value">{display}</span>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
 * PillStepper — pill with label + decrement button + number input + increment
 * ───────────────────────────────────────────────────────────────────────────── */

interface PillStepperProps {
  label?: string;
  min?: number;
  max?: number;
  value: number;
  onChange: (value: number) => void;
  'aria-label'?: string;
}

/** Pill containing a labelled numeric stepper (− value +) */
export function PillStepper({
  label,
  min = 0,
  max = 100,
  value,
  onChange,
  'aria-label': ariaLabel,
}: PillStepperProps) {
  const clamp = (v: number) => Math.max(min, Math.min(max, v));

  return (
    <div
      className="ui-pill-stepper"
      role="group"
      aria-label={ariaLabel ?? label}
    >
      {label && <span className="ui-pill__label">{label}</span>}
      <button
        className="ui-pill-stepper__btn"
        onClick={() => onChange(clamp(value - 1))}
        disabled={value <= min}
        aria-label="Decrease"
      >
        &minus;
      </button>
      <input
        className="ui-pill-stepper__input"
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          const v = clamp(Number(e.target.value) || min);
          onChange(v);
        }}
        aria-label={ariaLabel ?? label}
      />
      <button
        className="ui-pill-stepper__btn"
        onClick={() => onChange(clamp(value + 1))}
        disabled={value >= max}
        aria-label="Increase"
      >
        +
      </button>
    </div>
  );
}
